import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue } from 'firebase/database';
import { auth } from '../firebase';
import { subscribeToWebLicense, saveWebUserData } from '../services/webLicenseService';

import { doc, getFirestore, onSnapshot } from 'firebase/firestore';

const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxx-xxxx-4xxx-yxxx-xxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const useAuth = () => {
    const [user, setUser] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPremium, setIsPremium] = useState(false);
    const [isLicencesPremium, setIsLicencesPremium] = useState(false);
    const [isWebPremium, setIsWebPremium] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [deviceId, setDeviceId] = useState(null);
    const [webLicenseData, setWebLicenseData] = useState(null);
    const [cursoPeriodoActual, setCursoPeriodoActual] = useState('p1');
    const [cursoPeriodoAcceso, setCursoPeriodoAcceso] = useState(null);
    const [isCursoPeriodoActivo, setIsCursoPeriodoActivo] = useState(false);

    const encodeEmail = (email) => {
        return email ? email.replace(/\./g, '_dot_').replace(/@/g, '_at_') : '';
    };

    useEffect(() => {
        let storedId = localStorage.getItem('vortex_browser_id');
        if (!storedId) {
            storedId = `WEB_${generateUUID()}`;
            localStorage.setItem('vortex_browser_id', storedId);
        }
        setDeviceId(storedId);

        let unsubscribeProfile = null;

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);

            if (currentUser && currentUser.email) {
                const db = getDatabase();
                const firestore = getFirestore();
                const emailKey = encodeEmail(currentUser.email);

                // Suscripción al perfil del usuario en Firestore (Realtime)
                const userDocRef = doc(firestore, 'users', currentUser.uid);
                unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setProfileData(docSnap.data());
                    } else {
                        setProfileData({ institucion: 'SEMAR' });
                    }
                }, (error) => {
                    console.error("Error fetching user profile:", error);
                    setProfileData({ institucion: 'SEMAR' });
                });

                // Suscripción a licencia de app móvil
                const checkLicense = (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const now = Date.now();
                        // Lógica sincronizada con Android: pEnd == 0 o pEnd > now
                        // Además, somos más flexibles con isActive/isPremium (default true si no están)
                        const isActive = data.isActive !== false;
                        const isPremiumField = data.isPremium !== false;
                        const isNotExpired = data.premiumEndDate === 0 || data.premiumEndDate > now;
                        
                        return isActive && isPremiumField && isNotExpired;
                    }
                    return false;
                };

                const emailLicenseRef = ref(db, `licencesPremium/${emailKey}`);
                const uidLicenseRef = ref(db, `licencesPremium/${currentUser.uid}`);

                // Periodo actual autoritativo (config RTDB) para acceso a cursos
                const periodoActualRef = ref(db, 'config/cursoPeriodoActual');
                const periodoActualUnsub = onValue(periodoActualRef, (snap) => {
                    setCursoPeriodoActual(snap.val() || 'p1');
                });

                let emailLicenseActive = false;
                let uidLicenseActive = false;
                let webLicenseActive = false;

                const updateCombinedStatus = () => {
                    const licencesPremiumActive = emailLicenseActive || uidLicenseActive;
                    setIsLicencesPremium(licencesPremiumActive);
                    setIsPremium(licencesPremiumActive || webLicenseActive);
                    setLoading(false);
                };

                const emailUnsubscribe = onValue(emailLicenseRef, (snapshot) => {
                    emailLicenseActive = checkLicense(snapshot);
                    const d = snapshot.val();
                    if (d && d.cursoPeriodoAcceso) setCursoPeriodoAcceso(d.cursoPeriodoAcceso);
                    updateCombinedStatus();
                }, (error) => {
                    console.error("Error fetching email license:", error);
                    emailLicenseActive = false;
                    updateCombinedStatus();
                });

                const uidUnsubscribe = onValue(uidLicenseRef, (snapshot) => {
                    uidLicenseActive = checkLicense(snapshot);
                    const d = snapshot.val();
                    if (d && d.cursoPeriodoAcceso) setCursoPeriodoAcceso(d.cursoPeriodoAcceso);
                    updateCombinedStatus();
                }, (error) => {
                    console.error("Error fetching UID license:", error);
                    uidLicenseActive = false;
                    updateCombinedStatus();
                });

                // Suscripción a licencia web en usersweb
                const webLicenseUnsubscribe = subscribeToWebLicense(currentUser.uid, (status) => {
                    webLicenseActive = status.isValid;
                    setIsWebPremium(status.isValid);
                    setWebLicenseData(status.license);
                    updateCombinedStatus();
                });

                // Guardar datos del usuario en usersweb
                saveWebUserData(currentUser.uid, {
                    email: currentUser.email,
                    displayName: currentUser.displayName,
                    photoURL: currentUser.photoURL
                });

                return () => {
                    emailUnsubscribe();
                    uidUnsubscribe();
                    periodoActualUnsub();
                    if (typeof webLicenseUnsubscribe === 'function') {
                        webLicenseUnsubscribe();
                    }
                    if (unsubscribeProfile) {
                        unsubscribeProfile();
                    }
                };
            } else {
                setIsPremium(false);
                setIsLicencesPremium(false);
                setIsWebPremium(false);
                setWebLicenseData(null);
                setProfileData(null);
                setCursoPeriodoAcceso(null);
                setIsCursoPeriodoActivo(false);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []); // Sin dependencias - solo se ejecuta una vez al montar

    // Acceso a cursos por periodo: cursoPeriodoAcceso debe coincidir con el periodo actual
    useEffect(() => {
        setIsCursoPeriodoActivo(!!cursoPeriodoAcceso && cursoPeriodoAcceso === cursoPeriodoActual);
    }, [cursoPeriodoAcceso, cursoPeriodoActual]);

    return {
        user,
        profileData,
        loading: loading || authLoading,
        isPremium,
        isLicencesPremium,
        isWebPremium,
        webLicenseData,
        deviceId,
        isAuthenticated: !!user,
        isCursoPeriodoActivo,
        cursoPeriodoActual,
        cursoPeriodoAcceso
    };
};